import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FilterCriteriaComponent } from '../filter-criteria/filter-criteria.component';
import { EligibleSystemsTableComponent } from '../eligible-systems-table/eligible-systems-table.component';
import {  TranslateModule } from '@ngx-translate/core';
import { FilterCriteriaService } from '../services/filter-criteria.service';
import { SharedService } from '../services/shared.service';
import { AllQueryFilterResponse, GetFilterQueryResponse } from '../../models/filter-payload.model';

interface ColumnConfigurationResponse {
  userSelectedColumn?: string[];
  allColumns?: string[];
}

interface SaveColumnConfigurationPayload {
  userColumn: string[];
}

/** Sections available in the side pane navigator. */
type PaneSection = 'filter' | 'favoriteQuery' | 'favoriteTemplate';

/** Tabs available within the Query Filter section. */
type FilterTab = 'selected' | 'available';

@Component({
  selector: 'app-main-content',
  imports: [
    CommonModule,
    FilterCriteriaComponent,
    EligibleSystemsTableComponent,
    TranslateModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './main-content.component.html',
  styleUrls: ['./main-content.component.scss'],
})
export class MainContentComponent implements OnInit {
  /**
   * Minimum width (px) the side pane may end up at. `sh-pane` only clamps
   * the resize drag to a sane minimum when its `size` is the literal string
   * "resizable"; since this pane toggles between "condensed"/"expanded", a
   * drag on the resize handle can set an arbitrarily small internal
   * `resizedWidth` with no floor. That value is only reused (as the new
   * `size`) once the arrow is clicked to expand, so rather than fighting
   * the live drag we just correct the `size` after the fact in
   * `onPaneSizeChanged` if it ever resolves to something too small.
   */
  private static readonly MIN_PANE_WIDTH_PX = 352;

  configColumns: unknown = null;
  isConfigLoading = false;
  isSavingColumns = false;
  configLoadError = '';
  selectedColumns: string[] = [];
  availableColumns: string[] = [];

  /** Reference to the side pane so the icon rail can expand it programmatically. */
  @ViewChild('sidePane') sidePane?: ElementRef<HTMLElement & { size: string; resizedWidth?: string }>;

  /** Reference to the hosted filter-criteria editor so favourites can be loaded into it. */
  @ViewChild(FilterCriteriaComponent) filterCriteria?: FilterCriteriaComponent;

  /**
   * Expanded state of each accordion section. Sections are independent, so
   * opening one does not collapse the others.
   */
  expandedSections: Record<PaneSection, boolean> = {
    filter: true,
    favoriteQuery: false,
    favoriteTemplate: false,
  };

  /** Active tab inside the Query Filter section. */
  activeFilterTab: FilterTab = 'selected';

  /** Single global search text applied across all pane sections. */
  globalSearchText = '';

  onGlobalSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const sanitizedValue = (target?.value ?? '').replaceAll(/[^a-zA-Z0-9 .~-]/g, '');
    if (target && target.value !== sanitizedValue) {
      target.value = sanitizedValue;
    }
    this.globalSearchText = sanitizedValue;
  }

  /** User's favourite queries (standard filters) shown in the side pane. */
  favoriteQueries: GetFilterQueryResponse[] = [];
  /** User's favourite templates shown in the side pane. */
  favoriteTemplates: GetFilterQueryResponse[] = [];
  /** True while favourites are being (re)loaded from the backend. */
  isFavoritesLoading = false;

  constructor(
    private readonly filterCriteriaService: FilterCriteriaService,
    private readonly sharedService: SharedService,
  ) {}

  ngOnInit(): void {
    this.loadConfigColumns();
    this.loadFavorites();
  }

  /**
   * Open the requested section and make sure the pane itself is expanded.
   * Triggered from the condensed icon rail. Other sections are left untouched.
   * Only expands the pane/section here - the Load Template/Query modal is
   * only opened from `openFavoritesModal`, once the pane is already expanded
   * and the user clicks the accordion's icon or headline text directly.
   */
  openSectionFromRail(section: PaneSection): void {
    this.expandedSections[section] = true;
    const pane = this.sidePane?.nativeElement;
    if (pane) {
      pane.size = 'expanded';
    }
    if (section === 'favoriteQuery' || section === 'favoriteTemplate') {
      this.loadFavorites();
    }
  }

  /**
   * `sh-pane` reuses whatever `resizedWidth` was last recorded from a resize
   * drag as the new `size` whenever the expand/collapse arrow is clicked. If
   * that drag happened while the pane was condensed (a narrow icon rail), the
   * recorded width can be tiny with no floor applied, producing a squished
   * layout once expanded. Correct it back up to a sane minimum here, right
   * after the pane's `size` actually changes, without interfering with the
   * live drag itself.
   */
  onPaneSizeChanged(event: Event): void {
    const target = event.target as (HTMLElement & { size?: string; resizedWidth?: string }) | null;
    const size = target?.size;
    if (!target || !size || size.length > 20) {
      return;
    }
    const match = /^(\d+\.\d+|\.\d+|\d+)(px|rem|vw)$/.exec(size);
    if (!match) {
      return;
    }
    const value = Number.parseFloat(match[1]);
    const unit = match[2];
    let valueInPx = value;
    if (unit === 'rem') {
      valueInPx = value * 16;
    } else if (unit === 'vw') {
      valueInPx = (value / 100) * window.innerWidth;
    }
    if (valueInPx < MainContentComponent.MIN_PANE_WIDTH_PX) {
      const clamped = `${MainContentComponent.MIN_PANE_WIDTH_PX}px`;
      target.size = clamped;
      target.resizedWidth = clamped;
    }
  }

  /**
   * Sync a section's expanded state when the user toggles it via its header.
   * Sections are independent, so toggling one does not affect the others.
   */
  onAccordionExpandedChange(section: PaneSection, event: Event): void {
    const target = event.target as (HTMLElement & { expanded?: boolean }) | null;
    this.expandedSections[section] = Boolean(target?.expanded);
    if (
      this.expandedSections[section] &&
      (section === 'favoriteQuery' || section === 'favoriteTemplate')
    ) {
      this.loadFavorites();
    }
  }

  /** Switch between the Selected and Available tabs of the Query Filter section. */
  setActiveFilterTab(tab: FilterTab): void {
    this.activeFilterTab = tab;
  }

  /**
   * Toggle the favourite state of an entry. In this pane every entry is already
   * a favourite, so toggling removes it from the user's favourites and from the
   * list once the backend confirms.
   */
  toggleFavorite(entry: GetFilterQueryResponse, event?: Event): void {
    event?.stopPropagation();
    const nextState = !entry.isFavourite;
    this.filterCriteriaService.setFavorite(entry.guid, nextState).subscribe({
      next: () => {
        if (nextState) {
          entry.isFavourite = true;
          return;
        }
        this.favoriteQueries = this.favoriteQueries.filter((item) => item.guid !== entry.guid);
        this.favoriteTemplates = this.favoriteTemplates.filter((item) => item.guid !== entry.guid);
      },
    });
  }

  /** Load the selected favourite query/template into the filter-criteria editor. */
  importFavorite(entry: GetFilterQueryResponse, event?: Event, isTemplate: boolean = false): void {
    event?.stopPropagation();
    this.filterCriteria?.loadFilterFromFavorite(entry, isTemplate);
  }

  /**
   * Open the "Load Template/Query" modal (hosted by the filter-criteria editor)
   * pre-selected on the matching tab, but only when the click originated from
   * the accordion's icon or headline text - not from the arrow, the rest of
   * the header whitespace, or the favourites list body.
   *
   * The accordion's built-in label element stretches to fill the header row
   * (it renders as `width: 100%`), so checking the element's own bounding box
   * is not enough to exclude clicks on the whitespace next to the text. We
   * additionally measure the actual rendered glyphs of the label via a Range
   * and only treat the click as a text click if it falls within them.
   */
  openFavoritesModal(section: 'favoriteQuery' | 'favoriteTemplate', event: MouseEvent): void {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const iconTarget = path.find(
      (node): node is HTMLElement => node instanceof HTMLElement && node.classList.contains('accordion-icon'),
    );
    const labelTarget = path.find(
      (node): node is HTMLElement => node instanceof HTMLElement && node.classList.contains('accordion-label'),
    );

    const clickedIcon = !!iconTarget && this.isPointInRect(event.clientX, event.clientY, iconTarget.getBoundingClientRect());
    const clickedLabelText = !!labelTarget && this.isPointWithinRenderedText(labelTarget, event.clientX, event.clientY);

    if (!clickedIcon && !clickedLabelText) {
      return;
    }
    const tab = section === 'favoriteQuery' ? 'query' : 'template';
    this.filterCriteria?.openTemplateModal(tab);
  }

  /**
   * Keyboard equivalent of `openFavoritesModal`. Mouse-based hit-testing
   * (icon/text bounding boxes) doesn't apply to keyboard activation, so
   * Enter/Space on a focused accordion header always opens the modal.
   */
  onAccordionKeydown(section: 'favoriteQuery' | 'favoriteTemplate', event: Event): void {
    event.preventDefault();
    const tab = section === 'favoriteQuery' ? 'query' : 'template';
    this.filterCriteria?.openTemplateModal(tab);
  }

  private isPointInRect(x: number, y: number, rect: DOMRect): boolean {
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }

  /**
   * Checks whether the given point falls within the actual rendered glyphs of
   * the label element's text content, rather than the element's own box
   * (which stretches to fill the header row).
   */
  private isPointWithinRenderedText(label: HTMLElement, x: number, y: number): boolean {
    const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT);
    let textNode: Node | null;
    while ((textNode = walker.nextNode())) {
      if (!textNode.textContent?.trim()) {
        continue;
      }
      const range = document.createRange();
      range.selectNodeContents(textNode);
      const rects = range.getClientRects();
      for (const rect of rects) {
        if (this.isPointInRect(x, y, rect)) {
          return true;
        }
      }
    }
    return false;
  }

  /** Selected columns filtered by the global search text. */
  get filteredSelectedColumns(): string[] {
    return this.filterColumns(this.selectedColumns, this.globalSearchText);
  }

  /** Available columns filtered by the global search text. */
  get filteredAvailableColumns(): string[] {
    return this.filterColumns(this.availableColumns, this.globalSearchText);
  }

  /** Favorite queries filtered by the global search text. */
  get filteredFavoriteQueries(): GetFilterQueryResponse[] {
    return this.filterFavorites(this.favoriteQueries, this.globalSearchText);
  }

  /** Favorite templates filtered by the global search text. */
  get filteredFavoriteTemplates(): GetFilterQueryResponse[] {
    return this.filterFavorites(this.favoriteTemplates, this.globalSearchText);
  }

  private filterColumns(columns: string[], search: string): string[] {
    const term = search.trim().toLowerCase();
    if (!term) {
      return columns;
    }
    return columns.filter((column) => column.toLowerCase().includes(term));
  }

  private filterFavorites(entries: GetFilterQueryResponse[], search: string): GetFilterQueryResponse[] {
    const term = search.trim().toLowerCase();
    if (!term) {
      return entries;
    }
    return entries.filter((entry) => (entry.name ?? '').toLowerCase().includes(term));
  }

  /**
   * Fetch the user's saved filters/templates and keep only the ones flagged as
   * favourite, sorted alphabetically by name for a stable pane ordering.
   * Public so it can also be triggered from the template (e.g. when the
   * hosted filter-criteria editor reports a favourite was toggled).
   */
  loadFavorites(): void {
    this.isFavoritesLoading = true;
    this.filterCriteriaService.getAllFilterQueries().subscribe({
      next: (response: AllQueryFilterResponse) => {
        this.favoriteQueries = this.pickFavorites(response?.filter);
        this.favoriteTemplates = this.pickFavorites(response?.template);
        this.isFavoritesLoading = false;
      },
      error: () => {
        this.isFavoritesLoading = false;
      },
    });
  }

  private pickFavorites(entries: GetFilterQueryResponse[] | undefined): GetFilterQueryResponse[] {
    return (entries ?? [])
      .filter((entry) => Boolean(entry?.isFavourite))
      .sort((left, right) =>
        (left.name ?? '').localeCompare(right.name ?? '', undefined, { sensitivity: 'base' }),
      );
  }

  private loadConfigColumns(): void {
    this.isConfigLoading = true;
    this.configLoadError = '';

    this.filterCriteriaService.getColumnConfiguration().subscribe({
      next: (response) => {
        this.configColumns = response;
        const columnResponse = response as ColumnConfigurationResponse;
        const selectedColumns = Array.isArray(columnResponse.userSelectedColumn)
          ? columnResponse.userSelectedColumn
          : [];
        const allColumns = Array.isArray(columnResponse.allColumns)
          ? columnResponse.allColumns
          : [];
        const selectedSet = new Set(selectedColumns);

        this.selectedColumns = [...selectedColumns];
        this.availableColumns = allColumns.filter(
          (column) => !selectedSet.has(column),
        );
        this.sharedService.setSelectedColumns(this.selectedColumns);
        this.isConfigLoading = false;
      },
      error: () => {
        this.configLoadError = 'Unable to load column configuration.';
        this.isConfigLoading = false;
      },
    });
  }

  onSelectedColumnChange(column: string, event: Event): void {
    if (this.resolveChecked(event)) {
      return;
    }

    this.selectedColumns = this.selectedColumns.filter((item) => item !== column);
    if (!this.availableColumns.includes(column)) {
      this.availableColumns = [...this.availableColumns, column];
    }
    this.sharedService.setSelectedColumns(this.selectedColumns);
  }

  onAvailableColumnChange(column: string, event: Event): void {
    // The item is rendered only in the available list, so any checkbox interaction
    // should move it to selected immediately.
    this.availableColumns = this.availableColumns.filter((item) => item !== column);
    if (!this.selectedColumns.includes(column)) {
      this.selectedColumns = [...this.selectedColumns, column];
    }
    this.sharedService.setSelectedColumns(this.selectedColumns);
  }

  onSubmitColumns(): void {
    const payload: SaveColumnConfigurationPayload = {
      userColumn: this.selectedColumns,
    };

    this.isSavingColumns = true;
    this.filterCriteriaService.saveColumnConfiguration(payload).subscribe({
      next: () => {
        this.isSavingColumns = false;
        this.isConfigLoading=false;
      },
      error: () => {
        this.isSavingColumns = false;
        this.configLoadError = 'Unable to save column configuration.';
      },
    });
  }

  private resolveChecked(event: Event): boolean {
    const customEvent = event as CustomEvent<{ checked?: boolean } | boolean>;
    if (typeof customEvent?.detail === 'boolean') {
      return customEvent.detail;
    }

    if (
      customEvent?.detail &&
      typeof customEvent.detail === 'object' &&
      'checked' in customEvent.detail
    ) {
      return Boolean(customEvent.detail.checked);
    }

    const inputTarget = event.target as HTMLInputElement | null;
    return Boolean(inputTarget?.checked);
  }
}
